import React, { useState } from 'react';
import { Bell, ChevronDown, Filter, MessageCircle, Search } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';

const AppFrame = styled('div')`
  width: 100%;
  overflow: hidden;
  border: 1px solid #dfe3e7;
  border-radius: 12px;
  background: #f4f4f4;
  box-shadow: 0 18px 55px rgba(14, 28, 43, 0.1);
`;

const AppHeader = styled('div')`
  display: flex;
  align-items: center;
  min-height: 51px;
  padding: 0 16px;
  border-bottom: 1px solid #dfe3e7;
  background: #fff;
`;

const AppLogo = styled('div')`
  margin-right: 18px;
  padding-right: 17px;
  border-right: 1px solid #dfe3e7;
  color: #121212;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: -0.04em;
`;

const AppNav = styled('div')`
  display: flex;
  align-items: stretch;
  align-self: stretch;
  gap: 16px;
  flex: 1;
  min-width: 0;

  @media (max-width: 720px) {
    gap: 9px;

    > *:nth-child(n + 4) {
      display: none;
    }
  }
`;

const NavButton = styled('button')<{ $active?: boolean }>`
  position: relative;
  border: 0;
  padding: 0;
  background: transparent;
  color: ${(p) => (p.$active ? '#171717' : '#7c8188')};
  font-size: 9px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  cursor: default;

  &::after {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    height: 2px;
    background: ${(p) => (p.$active ? COLORS.PRIMARY : 'transparent')};
    content: '';
  }
`;

const UtilityNav = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #1c2733;

  @media (max-width: 520px) {
    display: none;
  }
`;

const Avatar = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #94a3b8;
  color: #fff;
  font-size: 8px;
  font-weight: 800;
`;

const AppCanvas = styled('div')`
  min-height: 420px;
  padding: 14px 18px 18px;
  background: #f4f4f4;

  @media (max-width: 680px) {
    min-height: 0;
    padding: 12px;
  }
`;

const PerformanceLayout = styled('div')`
  display: grid;
  grid-template-columns: 82px minmax(0, 1fr);
  gap: 14px;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const ReportSidebar = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;

  @media (max-width: 620px) {
    display: none;
  }
`;

const SidebarGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:not(:first-child) {
    margin-top: 6px;
  }
`;

const PerformanceMain = styled('div')`
  min-width: 0;
`;

const PerformanceToolbar = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 35px;
  margin-bottom: 12px;
  color: #7d8389;
  font-size: 8px;

  strong {
    margin-right: 2px;
    color: #24282c;
    font-size: 14px;
  }

  @media (max-width: 760px) {
    flex-wrap: wrap;

    > span:nth-of-type(n + 3) {
      display: none;
    }
  }
`;

const TinySearch = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 125px;
  padding: 7px 9px;
  border: 1px solid #e1e4e8;
  border-radius: 5px;
  background: #fff;
  color: #a0a5ab;
`;

const ToolbarChip = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 6px 8px;
  border-radius: 5px;
  background: #fff;
  color: #5d6268;
  line-height: 1;

  svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const DateControl = styled('div')`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-left: auto;
  padding: 7px 10px;
  border-radius: 5px;
  background: #fff;
  color: #5d6268;
  font-size: 8px;
`;

const ReportGrid = styled('div')`
  display: grid;
  grid-template-columns: 31% minmax(0, 1fr);
  gap: 12px;
  margin-bottom: 14px;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled('div')`
  padding: 16px 14px;
  border-radius: 6px;
  background: #fff;
  border: 1px solid #eceef1;
`;

const CellTitle = styled('div')`
  margin-bottom: 14px;
  color: #2d3338;
  font-size: 9px;
  font-weight: 700;
`;

const EarningsTotal = styled('div')`
  margin: 8px 0 10px;
  color: #24282c;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.1;
  text-align: center;
  letter-spacing: -0.02em;
`;

const SkeletonLine = styled('div')<{ $width?: string; $height?: string }>`
  border-radius: 4px;
  background: #e8ebee;
  width: ${(p) => p.$width || '100%'};
  height: ${(p) => p.$height || '10px'};
`;

const SidebarSkeleton = styled(SkeletonLine)<{ $active?: boolean }>`
  background: ${(p) => (p.$active ? '#d4e8f7' : '#e8ebee')};
`;

const NavItem = styled('span')`
  display: inline-flex;
  align-items: center;
  align-self: stretch;
`;

const NavSkeletonBar = styled(SkeletonLine)`
  align-self: center;
`;

const StatusRows = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 18px;
`;

const StatusRow = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StatusTrack = styled('div')`
  height: 6px;
  overflow: hidden;
  border-radius: 3px;
  background: #eef1f4;
`;

const StatusFill = styled('div')<{ $width: number }>`
  width: ${(p) => p.$width}%;
  height: 100%;
  border-radius: 3px;
  background: #d5dbe1;
`;

const ChartArea = styled('div')`
  position: relative;
  height: 220px;
  padding-top: 8px;
`;

const ChartBars = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 5px;
  height: 100%;
`;

const StackedBarButton = styled('button')<{ $height: number; $active: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column-reverse;
  height: ${(p) => p.$height}%;
  min-height: 24px;
  border: 0;
  padding: 0;
  background: transparent;
  border-radius: 3px 3px 0 0;
  overflow: hidden;
  cursor: pointer;
  box-shadow: ${(p) =>
    p.$active ? `inset 0 0 0 1px ${COLORS.PRIMARY}` : 'none'};

  &:focus-visible {
    outline: 2px solid ${COLORS.PRIMARY};
    outline-offset: 2px;
  }
`;

const StackSegment = styled('span')<{ $color: string; $flex: number }>`
  flex: ${(p) => p.$flex};
  width: 100%;
  min-height: 3px;
  background: ${(p) => p.$color};
`;

const ChartTooltip = styled('div')<{ $left: number }>`
  position: absolute;
  top: 0;
  left: ${(p) => p.$left}%;
  z-index: 2;
  min-width: 148px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #1a2332;
  color: #f8fafc;
  font-size: 9px;
  line-height: 1.45;
  box-shadow: 0 10px 28px rgba(8, 15, 28, 0.35);
  transform: translate(-50%, 0);
  pointer-events: none;

  &::after {
    position: absolute;
    bottom: -5px;
    left: 50%;
    width: 10px;
    height: 10px;
    background: #1a2332;
    transform: translateX(-50%) rotate(45deg);
    content: '';
  }
`;

const TooltipTitle = styled('div')`
  margin-bottom: 8px;
  color: #94a3b8;
  font-size: 8px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const TooltipRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 5px;

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const TooltipLabel = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #e2e8f0;
`;

const TooltipSwatch = styled('span')<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

const TooltipTotal = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
  color: #fff;
  font-weight: 700;
`;

const ChartHeading = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  color: #34393f;
  font-size: 8px;
  font-weight: 700;
`;

const DateRows = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 4px;
`;

const DateRow = styled('div')`
  display: grid;
  grid-template-columns: minmax(140px, 1fr) repeat(4, 52px) 64px;
  gap: 12px;
  align-items: center;

  @media (max-width: 660px) {
    grid-template-columns: minmax(120px, 1fr) 64px;

    span:not(:last-child):not(:first-child) {
      display: none;
    }
  }
`;

const NETWORK_COLORS = {
  linkby: '#ec4899',
  avantlink: '#2563eb',
  cj: '#047857'
} as const;

const NETWORK_LABELS = {
  linkby: 'Linkby',
  avantlink: 'AvantLink',
  cj: 'CJ'
} as const;

type DayEarnings = {
  label: string;
  linkby: number;
  avantlink: number;
  cj: number;
};

const CHART_DAYS: DayEarnings[] = [
  { label: 'Apr 19', linkby: 18.2, avantlink: 32.4, cj: 41.6 },
  { label: 'Apr 20', linkby: 22.5, avantlink: 28.1, cj: 38.9 },
  { label: 'Apr 21', linkby: 14.8, avantlink: 19.6, cj: 26.4 },
  { label: 'Apr 22', linkby: 31.2, avantlink: 44.7, cj: 52.3 },
  { label: 'Apr 23', linkby: 26.4, avantlink: 36.8, cj: 48.1 },
  { label: 'Apr 24', linkby: 42.6, avantlink: 58.3, cj: 71.4 },
  { label: 'Apr 25', linkby: 19.7, avantlink: 27.5, cj: 33.8 },
  { label: 'Apr 26', linkby: 28.9, avantlink: 39.2, cj: 46.5 },
  { label: 'Apr 27', linkby: 24.1, avantlink: 31.6, cj: 40.2 },
  { label: 'Apr 28', linkby: 35.4, avantlink: 47.8, cj: 55.9 },
  { label: 'Apr 29', linkby: 16.3, avantlink: 22.4, cj: 29.7 },
  { label: 'Apr 30', linkby: 29.8, avantlink: 41.5, cj: 49.6 }
];

const formatUsd = (amount: number) =>
  `$${amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

const dayTotal = (day: DayEarnings) => day.linkby + day.avantlink + day.cj;

const maxDayTotal = Math.max(...CHART_DAYS.map(dayTotal));

const EarningsChart = () => {
  const [activeIndex, setActiveIndex] = useState(5);
  const activeDay = CHART_DAYS[activeIndex];
  const tooltipLeft = ((activeIndex + 0.5) / CHART_DAYS.length) * 100;

  return (
    <ChartArea>
      <ChartTooltip $left={tooltipLeft} aria-hidden>
        <TooltipTitle>{activeDay.label}</TooltipTitle>
        {(Object.keys(NETWORK_LABELS) as Array<keyof typeof NETWORK_LABELS>).map(
          (key) => (
            <TooltipRow key={key}>
              <TooltipLabel>
                <TooltipSwatch $color={NETWORK_COLORS[key]} />
                {NETWORK_LABELS[key]}
              </TooltipLabel>
              <span>{formatUsd(activeDay[key])}</span>
            </TooltipRow>
          )
        )}
        <TooltipTotal>
          <span>Daily total</span>
          <span>{formatUsd(dayTotal(activeDay))}</span>
        </TooltipTotal>
      </ChartTooltip>
      <ChartBars>
        {CHART_DAYS.map((day, index) => {
          const total = dayTotal(day);
          const height = Math.round((total / maxDayTotal) * 100);

          return (
            <StackedBarButton
              key={day.label}
              type="button"
              $height={height}
              $active={index === activeIndex}
              aria-label={`${day.label}: ${formatUsd(total)}`}
              aria-pressed={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onFocus={() => setActiveIndex(index)}
            >
              <StackSegment $color={NETWORK_COLORS.cj} $flex={day.cj} />
              <StackSegment $color={NETWORK_COLORS.avantlink} $flex={day.avantlink} />
              <StackSegment $color={NETWORK_COLORS.linkby} $flex={day.linkby} />
            </StackedBarButton>
          );
        })}
      </ChartBars>
    </ChartArea>
  );
};

const TransactionsView = () => (
  <PerformanceLayout>
    <ReportSidebar aria-hidden>
      <SidebarGroup>
        <SkeletonLine $width="58%" $height="7px" />
        <SidebarSkeleton $active $width="88%" $height="9px" />
        <SidebarSkeleton $width="76%" $height="9px" />
        <SidebarSkeleton $width="52%" $height="9px" />
        <SidebarSkeleton $width="64%" $height="9px" />
      </SidebarGroup>
      <SidebarGroup>
        <SkeletonLine $width="62%" $height="7px" />
        <SidebarSkeleton $width="80%" $height="9px" />
        <SidebarSkeleton $width="58%" $height="9px" />
      </SidebarGroup>
      <SkeletonLine
        $width="100%"
        $height="28px"
        style={{ marginTop: 6, borderRadius: 5 }}
      />
    </ReportSidebar>
    <PerformanceMain>
      <PerformanceToolbar>
        <strong>Transactions</strong>
        <TinySearch>
          <Search size={11} /> Search transactions
        </TinySearch>
        <ToolbarChip>
          Group <ChevronDown size={10} />
        </ToolbarChip>
        <ToolbarChip>
          Sort Day <ChevronDown size={10} />
        </ToolbarChip>
        <ToolbarChip>
          <Filter size={10} /> Filters
        </ToolbarChip>
        <DateControl>
          Last 30 days <ChevronDown size={11} />
        </DateControl>
      </PerformanceToolbar>
      <ReportGrid>
        <Panel>
          <CellTitle>Total earnings</CellTitle>
          <EarningsTotal>$76,394.01</EarningsTotal>
          <SkeletonLine
            $width="54%"
            $height="10px"
            style={{ margin: '10px auto 0' }}
          />
          <StatusRows>
            {[78, 52, 18, 12].map((width) => (
              <StatusRow key={width}>
                <SkeletonLine $width="68%" $height="8px" />
                <StatusTrack>
                  <StatusFill $width={width} />
                </StatusTrack>
              </StatusRow>
            ))}
          </StatusRows>
        </Panel>
        <Panel>
          <ChartHeading>
            <span>Earnings</span>
            <span>Day ⌄ &nbsp; By platform ⌄</span>
          </ChartHeading>
          <EarningsChart />
        </Panel>
      </ReportGrid>
      <DateRows aria-hidden>
        {[0, 1, 2, 3, 4].map((index) => (
          <DateRow key={index}>
            <SkeletonLine $width={`${88 - index * 6}%`} $height="9px" />
            <SkeletonLine $width="100%" $height="8px" />
            <SkeletonLine $width="100%" $height="8px" />
            <SkeletonLine $width="100%" $height="8px" />
            <SkeletonLine $width="100%" $height="8px" />
            <SkeletonLine $width="100%" $height="9px" />
          </DateRow>
        ))}
      </DateRows>
    </PerformanceMain>
  </PerformanceLayout>
);

export const RevenueWorkspaceMockup = () => (
  <AppFrame>
    <AppHeader>
      <AppLogo>affilimate</AppLogo>
      <AppNav aria-label="Affilimate product navigation">
        <NavItem aria-hidden>
          <NavSkeletonBar $width="42px" $height="8px" />
        </NavItem>
        <NavItem aria-hidden>
          <NavSkeletonBar $width="48px" $height="8px" />
        </NavItem>
        <NavButton type="button" $active>
          Performance
        </NavButton>
        <NavItem aria-hidden>
          <NavSkeletonBar $width="36px" $height="8px" />
        </NavItem>
        <NavItem aria-hidden>
          <NavSkeletonBar $width="28px" $height="8px" />
        </NavItem>
        <NavItem aria-hidden>
          <NavSkeletonBar $width="56px" $height="8px" />
        </NavItem>
        <NavItem aria-hidden>
          <NavSkeletonBar $width="40px" $height="8px" />
        </NavItem>
      </AppNav>
      <UtilityNav aria-hidden>
        <MessageCircle size={13} />
        <Bell size={13} />
        <Avatar>AM</Avatar>
      </UtilityNav>
    </AppHeader>
    <AppCanvas>
      <TransactionsView />
    </AppCanvas>
  </AppFrame>
);
